import { Accordion, AccordionItemProps, AccordionItem as Item } from '@szhsin/react-accordion'
import { default as classNames, default as cx } from 'classnames'
import { observer } from 'mobx-react-lite'
import React, { ReactNode, useEffect, useMemo } from 'react'
import { atom, useRecoilValue, useSetRecoilState } from 'recoil'
import { Link, useLocation } from 'react-router-dom'
import { useUser } from '../../hooks/useUser'
import { ClassValue } from 'classnames/types'

import {
    AdManageIcon,
    AutomationsIcon,
    DashboardIcon,
    HistoryIcon,
    IconProps,
    SegmentsIcon,
    SettingsIcon,
    ToolsIcon,
} from '../../icons/icons'
import { shouldShowRecipesNavItem } from '../../util/account-gates'
import { navStore } from '../nav/Nav'
import { PreservingLink } from '../ui/PreservingLink'

const navData: { [key: string]: NavItem[] } = {
    dashboards: [
        'Active Ad Account',
        'All Ad Accounts',
    ],
    tools: ['Chat', 'Smart Campaigns', 'Recipes', 'N-Grams'],
    'ad manager': [
        'Campaigns',
        'Portfolios',
        'Placements',
        'Ad Groups',
        'Ads',
        'Keywords',
        'Targets',
        'Search Terms',
        'Products',
    ],
}

const adminNavData: { [key: string]: NavItem[] } = {
    admin: ['Users', 'Public Recipes', 'Bulk Actions Manager', 'Recipes Manager', 'Updates'],
}

interface AccordionItemPropsExt extends AccordionItemProps {
    header: JSX.Element
    onCollapse: () => void
    onEnter: () => void
    isLast: boolean
}

const AccordionItem = ({
    header,
    onCollapse,
    onEnter,
    isLast,
    ...rest
}: AccordionItemPropsExt): JSX.Element => {
    return (
        <Item
            {...rest}
            header={(s) => {
                if (s.state.status === 'exited') {
                    onCollapse()
                } else if (s.state.status === 'entered') {
                    onEnter()
                }
                return (
                    <>
                        {header}
                        <img className="chevron" src={'/chevron.png'} alt="Chevron Down" />
                    </>
                )
            }}
            className={classNames('item', {
                itemLast: isLast,
            })}
            buttonProps={{
                className: ({ isEnter }) =>
                    classNames('itemBtn', {
                        itemBtnExpanded: isEnter,
                    }),
            }}
            contentProps={{ className: 'itemContent' }}
            panelProps={{ className: 'itemPanel' }}
        />
    )
}

export type NavItem =
    | 'Active Ad Account'
    | 'All Ad Accounts'
    | 'Smart Campaigns'
    | 'Recipes'
    | 'Segments'
    | 'Campaigns'
    | 'Portfolios'
    | 'Placements'
    | 'Ad Groups'
    | 'Ads'
    | 'Product Ads'
    | 'Keywords'
    | 'Targets'
    | 'Search Terms'
    | 'Products'
    | 'Logs'
    | 'Audit Logs'
    | 'Chat'
    | 'Recipe Copilot'
    | 'Quick Create'
    | 'Export ASINs'
    | 'Feature Request'
    | 'Keyword Recommendations (beta)'
    | 'Bid Recommendations (beta)'
    | 'Product Overview (beta)'
    | 'N-Grams'
    | 'Users'
    | 'Public Recipes'
    | 'Bulk Actions Manager'
    | 'Recipes Manager'
    | 'Updates'
    | 'Ad Account Settings'
    | 'History'

interface AccordionProps {
    parent: string
    active: NavItem | undefined
}

type Icon = (p: IconProps) => JSX.Element

// Renders the accordion parent element. This is the clickable header with
// an optional icon.
const AccordionHeader = ({ parent, active }: AccordionProps) => {
    let icon: undefined | Icon = undefined

    switch (parent.toLowerCase()) {
        case 'dashboards':
            icon = DashboardIcon
            break
        case 'tools':
            icon = ToolsIcon
            break
        case 'ad manager':
        case 'manage':
            icon = AdManageIcon
            break
    }

    if (icon) {
        const Icon = icon
        return (
            <div className="flex items-center gap-2 text-[11px] font-[650] uppercase tracking-[0.08em] text-[#7289A3]">
                <Icon isActive={active && navData[parent.toLowerCase()].includes(active)} />
                <span>{parent}</span>
            </div>
        )
    }

    return (
        <span className="text-[11px] font-[650] uppercase tracking-[0.08em] text-[#7289A3]">
            {parent}
        </span>
    )
}

export const Navbar = observer(
    ({
        location,
    }: {
        location?: string
    }) => {
        const { user } = useUser()
        const setActiveNav = useSetRecoilState(activeNavState)
        const activeNavItem = useRecoilValue(activeNavState)

        useEffect(() => {
            const navItem = locationToMenuItemName(location)
            if (navItem) {
                setActiveNav(navItem)
            }
        }, [location])

        // Append the admin only nav items if the user is an admin
        const finalNavData = useMemo(() => {
            const baseNavData = shouldShowRecipesNavItem(user)
                ? navData
                : {
                      ...navData,
                      tools: navData.tools.filter((item) => item !== 'Recipes'),
                  }

            if (user?.is_admin) {
                return {
                    ...adminNavData,
                    ...baseNavData,
                }
            }

            return baseNavData
        }, [user])

        const primaryAccordion = useMemo(() => {
            return <AccordionWrapper navData={finalNavData} active={activeNavItem} />
        }, [activeNavItem, finalNavData])

        const additionalNavItems = (
            <div>
                <AdditionalNav
                    to="/profile-settings"
                    name="Ad Account Settings"
                    icon={<SettingsIcon />}
                />
                <AdditionalNav to="/combined-history" name="History" icon={<HistoryIcon />} />
                <AdditionalNav
                    large
                    to="/segments"
                    name="Segments"
                    icon={
                        <div className="flex justify-center items-center w-6 h-6">
                            <SegmentsIcon isActive={activeNavItem === 'Segments'} />
                        </div>
                    }
                />
            </div>
        )

        return (
            <div>
                {additionalNavItems}
                {primaryAccordion}
            </div>
        )
    },
)

interface NavItemProps {
    name: NavItem
}

// A single, normal nav item
const NavItem = ({ name }: NavItemProps) => {
    const active = useRecoilValue(activeNavState)
    const to = menuItemNameToLocation(name)
    const id = name === 'Smart Campaigns' ? 'nav-smart-campaigns' : `nav-${convertToSlug(name)}`

    const className = useMemo(() => {
        return cx('mj-nav-link', {
            itemSelected: active == name,
        })
    }, [active])

    return (
        <div id={id}>
            {name === 'Smart Campaigns' ? (
                <Link className={className} to="/smart-campaigns">
                    {name}
                </Link>
            ) : (
                <PreservingLink className={className} to={to}>
                    {name}
                </PreservingLink>
            )}
        </div>
    )
}

const menuItemNameToLocation = (menuItemName?: NavItem): string => {
    if (menuItemName == null) {
        return ''
    }

    if (menuItemName == 'Feature Request') {
        window.open('https://bit.ly/MJ-Feature-Idea', '_blank')
        return ''
    }

    switch (menuItemName) {
        case 'Active Ad Account':
            return 'dashboard'
        case 'All Ad Accounts':
            return 'ad-accounts-overview'
        case 'Ads':
            return 'product-ads'
        case 'Recipe Copilot':
            return 'copilot'
        case 'Chat':
            return 'chat'
        case 'Quick Create':
            return 'tools/quick-create'
        case 'Export ASINs':
            return 'tools/export-asins'
        case 'Keyword Recommendations (beta)':
            return 'tools/keyword-recommendations'
        case 'Bid Recommendations (beta)':
            return 'tools/bid-recommendations'
        case 'Product Overview (beta)':
            return 'tools/product-overview'
        case 'N-Grams':
            return 'tools/ngrams'
        case 'Audit Logs':
        case 'Logs':
        case 'Campaigns':
        case 'Recipes':
        case 'Segments':
        case 'Portfolios':
        case 'Placements':
        case 'Ad Groups':
        case 'Keywords':
        case 'Targets':
        case 'Search Terms':
        case 'Products':
            return convertToSlug(menuItemName)
        case 'Smart Campaigns':
            return '/smart-campaigns'
        case 'Users':
            return 'admin/users'
        case 'Public Recipes':
            return 'admin/public-recipes'
        case 'Bulk Actions Manager':
            return 'admin/bulk-actions'
        case 'Recipes Manager':
            return 'admin/recipes'
        case 'Updates':
            return 'admin/updates'
        default:
            console.error(
                'No location set for menu item: ' + menuItemName + ' in menuItemNameToLocation',
            )
            return ''
    }
}

interface AccordionWrapperProps {
    navData: { [key: string]: NavItem[] }
    active: NavItem
}

const AccordionWrapper = observer(({ navData, active }: AccordionWrapperProps) => {
    return (
        <Accordion
            className="bg-white navbar-bottom-section px-3 py-2"
            transition
            transitionTimeout={250}
            allowMultiple
        >
            {Object.entries(navData).map(([parentKey, children], index) => (
                <AccordionItem
                    id={`nav-${convertToSlug(parentKey)}`}
                    isLast={Object.keys(navData).length == index + 1}
                    initialEntered={!navStore.shouldBeCollapsed(parentKey)}
                    key={index}
                    header={<AccordionHeader parent={parentKey} active={active} />}
                    onCollapse={() => navStore.removeAccordionState(parentKey)}
                    onEnter={() => navStore.addAccordionState(parentKey)}
                >
                    <div>
                        <div className="mj-nav-child-list m-0 block font-normal">
                            {children.map((childName, index) => (
                                <NavItem key={index} name={childName} />
                            ))}
                        </div>
                    </div>
                </AccordionItem>
            ))}
        </Accordion>
    )
})

/**
 * This gets used to resolve the current active menu item after a page has loaded.
 *
 * @param {string|null} location
 * @return {NavItem|undefined}
 */
const locationToMenuItemName = (location?: string): NavItem | undefined => {
    // Trim leading forward slash
    if (location != null && location.substring(0, 1) == '/') {
        location = location.substring(1)
    }

    switch (location) {
        case 'admin/users':
            return 'Users'
        case 'admin/updates':
            return 'Updates'
        case 'admin/bulk-actions':
            return 'Bulk Actions Manager'
        case 'admin/recipes':
            return 'Recipes Manager'
        case 'admin/public-recipes':
            return 'Public Recipes'
    }

    if (location != null) {
        // Get the root path, ignoring anything after a slash, such as a campaign id
        if (location.includes('/')) {
            location = location.split('/')[0]
        }
    }

    switch (location) {
        case 'dashboard':
            return 'Active Ad Account'
        case 'ad-accounts-overview':
            return 'All Ad Accounts'
        case 'settings':
            return 'Ad Account Settings'
        case 'profile-settings':
            return 'Ad Account Settings'
        case 'combined-history':
            return 'History'
        case 'product-ads':
            return 'Ads'
        case 'promotions':
        case 'smart-campaigns':
            return 'Smart Campaigns'
        case 'campaigns':
            return 'Campaigns'
        case 'recipes':
            return 'Recipes'
        case 'segments':
            return 'Segments'
        case 'portfolios':
            return 'Portfolios'
        case 'placements':
            return 'Placements'
        case 'ad-groups':
            return 'Ad Groups'
        case 'keywords':
            return 'Keywords'
        case 'targets':
            return 'Targets'
        case 'products':
            return 'Products'
        case 'search-terms':
            return 'Search Terms'
        case 'copilot':
            return 'Recipe Copilot'
        case 'chat':
            return 'Chat'
        case 'tools/quick-create':
            return 'Quick Create'
        case 'tools/export-asins':
            return 'Export ASINs'
        case 'tools/keyword-recommendations':
            return 'Keyword Recommendations (beta)'
        case 'tools/bid-recommendations':
            return 'Bid Recommendations (beta)'
        case 'tools/product-overview':
            return 'Product Overview (beta)'
        case 'tools/ngrams':
            return 'N-Grams'
        case 'audit-logs':
            return 'Audit Logs'
        case 'logs':
            return 'Logs'
        default:
            console.warn('Invalid location in locationToMenuItemName:', location)
            return undefined
    }
}

function convertToSlug(name: string): string {
    return name.toLowerCase().replace(/ /g, '-')
}

interface AdditionalNavProps {
    to: string
    name: NavItem
    icon: ReactNode
    large?: boolean
    className?: ClassValue
}

// A nav item with a custom icon. Has slightly different padding too.
const AdditionalNav = ({ to, name, icon, large, className }: AdditionalNavProps) => {
    const active = useRecoilValue(activeNavState)
    const setActiveNav = useSetRecoilState(activeNavState)
    const location = useLocation()
    const toPathname = useMemo(() => new URL(to, window.location.origin).pathname, [to])
    const isSelected =
        active == name ||
        location.pathname == toPathname ||
        location.pathname.startsWith(toPathname + '/')

    return (
        <PreservingLink
            id={`nav-${convertToSlug(name)}`}
            className={classNames('mj-nav-link mj-nav-link--top', className, {
                itemSelected: isSelected,
                'mj-nav-link--large': large,
            })}
            to={to}
            onClick={() => setActiveNav(name)}
        >
            {icon}
            <span
                className={classNames('pl-2', {
                    'font-normal': !large,
                    'font-medium': large,
                })}
            >
                {name}
            </span>
        </PreservingLink>
    )
}

const activeNavState = atom<NavItem>({
    key: 'activeNavState',
    default: 'Active Ad Account',
})
